/**
 * SalesIntelFunnelTile — super-admin dashboard card visualising the
 * `preview → confirm → dispatch` drop-off for the SalesIntelTile.
 * Powered by `/api/analytics/sales-intel/usage`.
 *
 * Sister tile to TourFunnelTile (same horizontal-bar pattern), but
 * smaller: this funnel has just 3 steps because the user journey is
 * Preview → Confirm Send → Dispatch. A "cancel" leakage indicator
 * shows up alongside Step 2 so PMs can see "how many people opened
 * the confirm dialog and bailed".
 *
 * Empty state renders a friendly hint rather than an error so the
 * tile doesn't look broken on a fresh deploy.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { LineChart, AlertTriangle, TrendingDown } from 'lucide-react';
import { useTelemetryTile } from '../../hooks';


const WINDOW_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

// Three logical funnel steps, each maps to a backend event slug. The
// "cancel" event is rendered as a side-leak on Step 2, not its own
// step (it's a non-progression signal).
const FUNNEL_STEPS = [
  { event: 'preview', label: 'Preview' },
  { event: 'send-now-confirm', label: 'Confirm dialog' },
  { event: 'send-now-dispatch', label: 'Dispatch' },
];


function formatPct(x) {
  if (typeof x !== 'number' || Number.isNaN(x)) return '—';
  return `${Math.round(x * 100)}%`;
}


/** Find the largest step-N → step-N+1 drop-off in unique users. */
function findBiggestDropOff(steps) {
  if (!steps || steps.length < 2) return null;
  let worst = null;
  for (let i = 0; i < steps.length - 1; i += 1) {
    const here = steps[i].users;
    const next = steps[i + 1].users;
    if (!here) continue;
    const lost = here - next;
    const lostPct = lost / here;
    if (lostPct > 0 && (!worst || lostPct > worst.lostPct)) {
      worst = { from: i, to: i + 1, lostPct };
    }
  }
  return worst;
}


export default function SalesIntelFunnelTile() {
  const [windowDays, setWindowDays] = useState(30);
  const { data, loading, error } = useTelemetryTile({
    url: `/analytics/sales-intel/usage?window_days=${windowDays}`,
    deps: [windowDays],
  });

  if (error === 'forbidden') return null;

  const usageByEvent = (data?.usage || []).reduce((acc, u) => {
    acc[u.event] = u;
    return acc;
  }, {});

  const steps = FUNNEL_STEPS.map((s) => ({
    event: s.event,
    label: s.label,
    users: usageByEvent[s.event]?.unique_users || 0,
    count: usageByEvent[s.event]?.count || 0,
  }));
  const cancelUsers = usageByEvent['send-now-cancel']?.unique_users || 0;

  const top = steps[0]?.users || 0;
  const worstDrop = findBiggestDropOff(steps);
  const dispatchRate = data?.funnel?.preview_to_dispatch_rate ?? 0;
  const totalEvents = data?.totals?.events ?? 0;

  return (
    <Card
      data-testid="sales-intel-funnel-tile"
      className="border-fuchsia-200 bg-gradient-to-br from-white to-fuchsia-50/40"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <LineChart className="h-4 w-4 text-fuchsia-500" />
              Sales Intel Tile Funnel
            </CardTitle>
            <CardDescription className="mt-0.5">
              Of SEs who *previewed* their briefing, how many actually fired the email? Shows where the meta-loop leaks.
            </CardDescription>
          </div>
          <div
            className="inline-flex bg-white border border-slate-200 rounded-md p-0.5"
            data-testid="sales-intel-funnel-window-selector"
          >
            {WINDOW_OPTIONS.map((o) => (
              <button
                key={o.days}
                type="button"
                onClick={() => setWindowDays(o.days)}
                className={
                  'px-2 py-0.5 text-[11px] font-medium rounded transition-colors ' +
                  (windowDays === o.days
                    ? 'bg-fuchsia-500 text-white'
                    : 'text-slate-600 hover:bg-slate-100')
                }
                data-testid={`sales-intel-funnel-window-${o.days}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-500" data-testid="sales-intel-funnel-loading">
            Loading…
          </p>
        ) : error ? (
          <p className="text-xs text-rose-600" data-testid="sales-intel-funnel-error">
            Couldn't load funnel.
          </p>
        ) : totalEvents === 0 ? (
          <div className="text-xs text-slate-500 leading-relaxed" data-testid="sales-intel-funnel-empty">
            No tile activity in the last {windowDays} days yet. The
            funnel will populate the first time a super-admin clicks
            <em> Preview my briefing</em>.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Previewers</p>
                <p className="text-lg font-bold text-fuchsia-700 tabular-nums">
                  {data?.funnel?.previewers ?? 0}
                </p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Dispatch rate</p>
                <p className="text-lg font-bold text-emerald-700 tabular-nums">
                  {formatPct(dispatchRate)}
                </p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Cancellations</p>
                <p className="text-lg font-bold text-rose-700 tabular-nums">{cancelUsers}</p>
              </div>
            </div>

            {worstDrop && (
              <div
                className="mb-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] leading-snug text-rose-800"
                data-testid="sales-intel-funnel-drop-callout"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />
                <span>
                  Biggest drop-off:{' '}
                  <strong>
                    {steps[worstDrop.from].label} → {steps[worstDrop.to].label}
                  </strong>
                  {' — '}
                  <strong>-{formatPct(worstDrop.lostPct)}</strong> of users bailed here.
                </span>
              </div>
            )}

            <ol className="space-y-1.5" data-testid="sales-intel-funnel-list">
              {steps.map((s, i) => {
                const pct = top ? s.users / top : 0;
                return (
                  <li
                    key={s.event}
                    className="flex items-center gap-2"
                    data-testid={`sales-intel-funnel-row-${i}`}
                  >
                    <span className="w-28 text-[11px] text-slate-700">
                      <span className="mr-1 font-mono text-slate-400">{i + 1}.</span>
                      {s.label}
                    </span>
                    <div className="relative flex-1 h-4 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-fuchsia-400 to-fuchsia-500 transition-all"
                        style={{ width: `${Math.round(pct * 100)}%` }}
                      />
                    </div>
                    <span className="w-10 text-right text-[11px] font-semibold text-fuchsia-700 tabular-nums">
                      {s.users}
                    </span>
                    {/* Cancel leak shows next to Step 2 (Confirm) since
                        a cancel happens *at* the confirm dialog. */}
                    {s.event === 'send-now-confirm' && cancelUsers > 0 && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[11px] text-rose-600 tabular-nums"
                        title={`${cancelUsers} user${cancelUsers === 1 ? '' : 's'} cancelled at the confirm dialog`}
                      >
                        <TrendingDown className="h-3 w-3" />
                        {cancelUsers}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </CardContent>
    </Card>
  );
}
